import { useCallback, useMemo } from "react";
import type { ZodType } from "zod";

import { useReferentiallyStableRawStorage } from "./use-referentially-stable-raw-storage";

type StorageEnvelope<T> = {
	data: T;
};

const deserialize = <T>(raw: string | null, schema: ZodType<T>, defaultValue?: T): T | undefined => {
	if (raw === null) {
		return defaultValue;
	}

	try {
		const data = (JSON.parse(raw) as StorageEnvelope<T>).data;
		const result = schema.safeParse(data);

		if (result.success === false) {
			return defaultValue;
		}

		return result.data;
	} catch {
		return defaultValue;
	}
};

export const useReferentiallyStableTypedStorage = <T>(
	storage: Storage,
	key: string,
	schema: ZodType<T>,
	defaultValue?: T,
) => {
	const { value: rawValue, set: rawSet, remove, fetch } = useReferentiallyStableRawStorage(storage, key);

	const value = useMemo(() => deserialize(rawValue, schema, defaultValue), [rawValue, schema, defaultValue]);

	const set = useCallback(
		(newValue: T) => {
			const envelope: StorageEnvelope<T> = { data: newValue };
			rawSet(JSON.stringify(envelope));
		},
		[rawSet],
	);

	return useMemo(() => ({ value, set, remove, fetch }), [value, set, remove, fetch]);
};
